import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { filter, map, take } from 'rxjs';
import { AuthService } from '../services/auth.service';

/**
 * Admin guard — gates all `/admin/*` routes.
 *
 * Group-based RBAC: users no longer have direct role bindings, so we rely on
 * the resolved `permissions` array shipped by /auth/me (which already includes
 * inheritance through groups + the `*` wildcard for platform.admin).
 * Falling back to `user.roles[].permissions` keeps legacy clients working.
 */
const ADMIN_PERMISSIONS = [
  'users.read',
  'users.write',
  'roles.read',
  'roles.write',
  'groups.read',
  'groups.write',
  'portals.read',
  'portals.write',
  'audit.read',
];

export const adminGuard: CanActivateFn = () => {
  const authService = inject(AuthService);
  const router = inject(Router);

  return authService.user$.pipe(
    filter(user => user !== null),
    take(1),
    map((user: any) => {
      // 1. Preferred: resolved effective permissions array from /auth/me
      const effective: string[] = Array.isArray(user?.permissions) ? user.permissions : [];
      if (
        effective.includes('*') ||
        effective.includes('platform.admin') ||
        ADMIN_PERMISSIONS.some(p => effective.includes(p))
      ) {
        return true;
      }

      // 2. Legacy fallback — direct role permissions
      const directPerms: string[] = (user?.roles ?? []).flatMap((r: any) => r.permissions ?? []);
      if (
        directPerms.includes('*') ||
        directPerms.includes('platform.admin') ||
        ADMIN_PERMISSIONS.some(p => directPerms.includes(p))
      ) {
        return true;
      }

      return router.createUrlTree(['/dashboard']);
    }),
  );
};
